import axios from 'axios';

/**
 * Stay 정보 타입 (기존과 동일)
 */
export interface StayInfo {
  id: string;
  stay_name: string;
  stay_id: string;
  address_id: string;
  created_date: string;
  updated_date: string;
  address: {
    id: string;
    address: string;
    detail_address: string;
    lat: number;
    lng: number;
    address_kr?: string;
    detail_address_kr?: string;
  };
}

/**
 * Polling 설정
 */
const POLLING_CONFIG = {
  INTERVAL: 3000, // 3초
  MAX_DURATION: 30 * 60 * 1000, // 30분
  PRODUCTION_API_URL: 'https://api.brainbackdoor.com'
};

/**
 * 백그라운드 Polling 서비스 클래스
 */
class BackgroundPollingService {
  private static instance: BackgroundPollingService;
  private intervalId: NodeJS.Timeout | null = null;
  private timeoutId: NodeJS.Timeout | null = null;
  private isPolling = false;
  private serviceUserId: string | null = null;
  private globalSuccessCallback: ((stayInfo: StayInfo) => void) | null = null;

  static getInstance(): BackgroundPollingService {
    if (!BackgroundPollingService.instance) {
      BackgroundPollingService.instance = new BackgroundPollingService();
    }
    return BackgroundPollingService.instance;
  }

  /**
   * 글로벌 성공 콜백 등록 (App.tsx에서 사용)
   */
  setGlobalSuccessCallback(callback: (stayInfo: StayInfo) => void): void {
    this.globalSuccessCallback = callback;
  }

  /**
   * 단일 polling 요청
   */
  private async pollOnce(): Promise<StayInfo | null> {
    if (!this.serviceUserId) {
      console.warn('[PollingService] No serviceUserId provided');
      return null;
    }

    try {
      const response = await axios.get(
        `${POLLING_CONFIG.PRODUCTION_API_URL}/api/stays/poll/${this.serviceUserId}`,
        { timeout: 10000 }
      );

      if (response.status === 200 && response.data) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[PollingService] Stay info received:', response.data);
        }
        return response.data as StayInfo;
      }
    } catch (error) {
      // 조용히 실패 처리 (404, 타임아웃 등은 정상적인 상황)
      if (process.env.NODE_ENV === 'development') {
        console.debug('[PollingService] Polling request failed (this is normal):', error);
      }
    }

    return null;
  }

  /**
   * Polling 시작
   */
  startPolling(serviceUserId: string): void {
    if (this.isPolling) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PollingService] Already polling, ignoring start request');
      }
      return;
    }

    this.serviceUserId = serviceUserId;
    this.isPolling = true;

    if (process.env.NODE_ENV === 'development') {
      console.log(`[PollingService] Starting polling for serviceUserId: ${serviceUserId}`);
    }

    // 주기적 polling 시작
    this.intervalId = setInterval(async () => {
      const stayInfo = await this.pollOnce();
      if (stayInfo) {
        this.stopPolling();
        
        // 글로벌 콜백 호출
        if (this.globalSuccessCallback) {
          this.globalSuccessCallback(stayInfo);
        }
      }
    }, POLLING_CONFIG.INTERVAL);

    // 30분 후 자동 중단
    this.timeoutId = setTimeout(() => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PollingService] Polling timeout after 30 minutes');
      }
      this.stopPolling();
    }, POLLING_CONFIG.MAX_DURATION);
  }

  /**
   * Polling 중단
   */
  stopPolling(): void {
    if (!this.isPolling) return;

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    this.isPolling = false;
    this.serviceUserId = null;

    if (process.env.NODE_ENV === 'development') {
      console.log('[PollingService] Polling stopped');
    }
  }

  /**
   * 현재 polling 상태 확인
   */
  isCurrentlyPolling(): boolean {
    return this.isPolling;
  }
}

// 싱글톤 인스턴스 export
export const pollingService = BackgroundPollingService.getInstance(); 